import { useEffect, useState, type ComponentProps, type Dispatch, type SetStateAction } from "react";
import { getAuthToken } from "../../lib/auth";
import PeoplePanel from "../../components/people/PeoplePanel";
import ChannelSidebar from "../../components/channels/ChannelSidebar";
import SpaceRail from '../../components/navigation/SpaceRail';
import MobileHeader from '../../components/navigation/MobileHeader';
import DmSidebar from '../../components/navigation/DmSidebar';
import NavigationShell from '../../app/NavigationShell';
import type { AuthUser } from "../../types/auth";
import type { useSpaces } from "./hooks/useSpaces";
import type { useInputModal } from "./hooks/useInputModal";

/** Desktop navigation chrome: space rail, mobile header, people panel, channel sidebar and DM sidebar. Pure presentation: all state stays in ChatLayout. */
export type DesktopSidebarsProps = {
  voiceJoined: boolean;
  voids: ReturnType<typeof useSpaces>["voids"];
  hiddenSpaces: ComponentProps<typeof SpaceRail>["hiddenSpaces"];
  spaceOrder: ComponentProps<typeof SpaceRail>["spaceOrder"];
  setSpaceOrder: ComponentProps<typeof SpaceRail>["setSpaceOrder"];
  unread: ComponentProps<typeof SpaceRail>["unread"];
  currentVoidId: string;
  me: ComponentProps<typeof SpaceRail>["me"];
  user: AuthUser | null;
  userQuickOpen: boolean;
  friendReqCount: ComponentProps<typeof SpaceRail>["friendReqCount"];
  unreadNotificationCount: ComponentProps<typeof SpaceRail>["unreadNotificationCount"];
  members: { id: string; name: string; username?: string; avatarUrl?: string | null; status?: string; nameColor?: string | null; role?: string; bio?: string; roles?: { id: string; name: string; color?: string | null }[] }[];
  currentChannelId: string;
  dmPageOpen: boolean;
  channelPaneView: "spaces" | "dms";
  setChanSheetOpen: (open: boolean) => void;
  setUsersSheetOpen: (open: boolean) => void;
  setChannelPaneView: (view: "spaces" | "dms") => void;
  setCurrentVoidId: (id: string) => void;
  setCurrentChannelId: (id: string) => void;
  setDmPageOpen: (open: boolean) => void;
  setDmSearch: ComponentProps<typeof DmSidebar>["setDmSearch"];
  setDmSearchOpen: ComponentProps<typeof DmSidebar>["setDmSearchOpen"];
  setNotificationsOpen: (open: boolean) => void;
  setFriendsOpen: (open: boolean) => void;
  switchVoid: (id: string) => void;
  switchChannel: (id: string) => void;
  toggleUserQuick: () => void;
  createSpace: () => void;
  showPeople: boolean;
  peopleW: ComponentProps<typeof PeoplePanel>["width"];
  /** Viewport is wide enough to keep the People column open by default. */
  isWideUp: boolean;
  startDrag: (which: "chan" | "people", clientX: number) => void;
  onlineChannelMembers: ComponentProps<typeof PeoplePanel>["onlineMembers"];
  offlineChannelMembers: ComponentProps<typeof PeoplePanel>["offlineMembers"];
  onOpenProfile: (targetId: string) => void;
  peoplePanelCollapsed: boolean;
  channelsLoading: boolean;
  peopleLoading: boolean;
  setPeoplePanelCollapsed: Dispatch<SetStateAction<boolean>>;
  isDmSpace: boolean;
  chanW: ComponentProps<typeof ChannelSidebar>["width"];
  isSpaceOwner: boolean;
  sidebarChannels: ComponentProps<typeof ChannelSidebar>["channels"];
  channelFolders: ComponentProps<typeof ChannelSidebar>["folders"];
  chanOrder: Record<string, string[]>;
  voicePresenceByChannel: ComponentProps<typeof ChannelSidebar>["voicePresence"];
  quickAddChannel: ComponentProps<typeof ChannelSidebar>["onQuickAddChannel"];
  createFolderInline: ComponentProps<typeof ChannelSidebar>["onCreateFolder"];
  askInput: ReturnType<typeof useInputModal>["askInput"];
  quickAddChannelToFolder: (folderId: string, name: string) => unknown;
  toggleFav: (voidId: string, channelId: string) => unknown;
  isFav: (voidId: string, channelId: string) => boolean;
  moveChannelToFolder: ComponentProps<typeof ChannelSidebar>["onMoveChannel"];
  renameChannelInline: ComponentProps<typeof ChannelSidebar>["onRenameChannel"];
  deleteChannelInline: ComponentProps<typeof ChannelSidebar>["onDeleteChannel"];
  handleChannelReorder: ComponentProps<typeof ChannelSidebar>["onChannelReorder"];
  handleFolderReorder: ComponentProps<typeof ChannelSidebar>["onFolderReorder"];
  spaceRoles: ComponentProps<typeof ChannelSidebar>["roles"];
  handleChannelAccessUpdate: ComponentProps<typeof ChannelSidebar>["onUpdateChannelAccess"];
  handleChannelLockToggle: ComponentProps<typeof ChannelSidebar>["onToggleChannelLock"];
  setDefaultChannel: ComponentProps<typeof ChannelSidebar>["onSetHomeChannel"];
  voiceDock: ComponentProps<typeof ChannelSidebar>["footer"];
  handleSelectChannel: ComponentProps<typeof ChannelSidebar>["onSelectChannel"];
  dmSearch: ComponentProps<typeof DmSidebar>["dmSearch"];
  dmSearchOpen: ComponentProps<typeof DmSidebar>["dmSearchOpen"];
  friendsOnline: ComponentProps<typeof DmSidebar>["friendsOnline"];
  friendsList: ComponentProps<typeof DmSidebar>["friendsList"];
  dmStartBusyUserId: ComponentProps<typeof DmSidebar>["dmStartBusyUserId"];
  activeDmVoids: ComponentProps<typeof DmSidebar>["activeDmVoids"];
  msgsByKey: ComponentProps<typeof DmSidebar>["msgsByKey"];
  notified: ComponentProps<typeof DmSidebar>["notified"];
  tabBgClass: ComponentProps<typeof DmSidebar>["tabBgClass"];
  getDmDisplayName: ComponentProps<typeof DmSidebar>["getDmDisplayName"];
  startDmWithUser: ComponentProps<typeof DmSidebar>["onStartDm"];
};

export function DesktopSidebars({
  voiceJoined, voids, hiddenSpaces, spaceOrder, setSpaceOrder, unread, currentVoidId, me, user, userQuickOpen, friendReqCount, unreadNotificationCount, members, currentChannelId, dmPageOpen, channelPaneView, setChanSheetOpen, setUsersSheetOpen, setChannelPaneView, setCurrentVoidId, setCurrentChannelId, setDmPageOpen, setDmSearch, setDmSearchOpen, setNotificationsOpen, setFriendsOpen, switchVoid, switchChannel, toggleUserQuick, createSpace, showPeople, peopleW, isWideUp, startDrag, onlineChannelMembers, offlineChannelMembers, onOpenProfile, peoplePanelCollapsed, setPeoplePanelCollapsed, channelsLoading, peopleLoading, isDmSpace, chanW, isSpaceOwner, sidebarChannels, channelFolders, chanOrder, voicePresenceByChannel, quickAddChannel, createFolderInline, askInput, quickAddChannelToFolder, toggleFav, isFav, moveChannelToFolder, renameChannelInline, deleteChannelInline, handleChannelReorder, handleFolderReorder, spaceRoles, handleChannelAccessUpdate, handleChannelLockToggle, setDefaultChannel, voiceDock, handleSelectChannel, dmSearch, dmSearchOpen, friendsOnline, friendsList, dmStartBusyUserId, activeDmVoids, msgsByKey, notified, tabBgClass, getDmDisplayName, startDmWithUser
}: DesktopSidebarsProps) {
  // On a narrow desktop window the People column starts collapsed so the chat keeps its room. Opening it there is
  // a temporary choice: it never overwrites the saved preference and resets when the window widens again.
  const [peopleOpenedNarrow, setPeopleOpenedNarrow] = useState(false);
  useEffect(() => { if (isWideUp) setPeopleOpenedNarrow(false); }, [isWideUp]);
  const peopleCollapsed = isWideUp ? peoplePanelCollapsed : !peopleOpenedNarrow;
  const togglePeople = () => {
    if (!isWideUp) { setPeopleOpenedNarrow(v => !v); return; }
    setPeoplePanelCollapsed(v => { const next = !v; try { localStorage.setItem('peoplePanelCollapsed', String(next)); } catch {} return next; });
  };
  return (
    <>
      <NavigationShell
        voiceDockVisible={voiceJoined}
        mobileHeader={
          <MobileHeader
            voids={voids}
            hiddenSpaces={hiddenSpaces}
            spaceOrder={spaceOrder}
            setSpaceOrder={setSpaceOrder}
            unread={unread}
            currentVoidId={currentVoidId}
            dmPageOpen={dmPageOpen}
            me={me}
            fallbackInitial={user?.username?.[0]}
            userQuickOpen={userQuickOpen}
            friendReqCount={friendReqCount}
            unreadNotificationCount={unreadNotificationCount}
            onHome={() => { setChanSheetOpen(false); setUsersSheetOpen(false); setChannelPaneView('spaces'); setCurrentVoidId(""); setCurrentChannelId("general"); }}
            onOpenDmPage={() => { setDmPageOpen(true); setDmSearch(''); setDmSearchOpen(false); }}
            onSwitchVoid={(id) => switchVoid(id)}
            onToggleUserQuick={toggleUserQuick}
            onOpenNotifications={() => setNotificationsOpen(true)}
          />
        }
        rail={
          <SpaceRail
            voids={voids}
            hiddenSpaces={hiddenSpaces}
            spaceOrder={spaceOrder}
            setSpaceOrder={setSpaceOrder}
            unread={unread}
            currentVoidId={currentVoidId}
            dmsActive={channelPaneView === 'dms'}
            me={me}
            fallbackInitial={user?.username?.[0]}
            meStatus={String(members.find(m => m.id === me.userId)?.status || 'online')}
            userQuickOpen={userQuickOpen}
            friendReqCount={friendReqCount}
            unreadNotificationCount={unreadNotificationCount}
            onHome={() => { setChanSheetOpen(false); setUsersSheetOpen(false); setChannelPaneView('spaces'); setCurrentVoidId(""); setCurrentChannelId("general"); }}
            onOpenDms={() => setChannelPaneView('dms')}
            onSwitchVoid={(id) => switchVoid(id)}
            onCreateSpace={createSpace}
            onToggleUserQuick={toggleUserQuick}
            onOpenNotifications={() => setNotificationsOpen(true)}
          />
        }
      />
      {/* Desktop members column (resizer + People list) — step 7.2 */}
      {showPeople && (
        <PeoplePanel
          width={peopleW}
          onResizeStart={(clientX) => startDrag('people', clientX)}
          onlineMembers={onlineChannelMembers}
          offlineMembers={offlineChannelMembers}
          onOpenProfile={onOpenProfile}
          collapsed={peopleCollapsed}
          onToggle={togglePeople}
          loading={peopleLoading}
        />
      )}

{/* Channels column (resizable) */}
{(() => {
  const showSpaceChannels = channelPaneView === 'spaces' && !!currentVoidId && !isDmSpace;
  const showChannelsColumn = showSpaceChannels;

  return (
    <>
      {showSpaceChannels && (
        <ChannelSidebar
          width={chanW}
          spaceName={voids.find(v=>v.id===currentVoidId)?.name || currentVoidId}
          isSpaceOwner={isSpaceOwner}
          isDmSpace={isDmSpace}
          loading={channelsLoading}
          currentVoidId={currentVoidId}
          currentChannelId={currentChannelId}
          channels={sidebarChannels}
          folders={channelFolders}
          channelOrder={chanOrder[currentVoidId] || []}
          unread={unread}
          voicePresence={voicePresenceByChannel}
          members={members}
          homeChannelId={voids.find(v=>v.id===currentVoidId)?.homeChannelId || null}
          onSelectChannel={handleSelectChannel}
          onQuickAddChannel={quickAddChannel}
          onCreateFolder={createFolderInline}
          onAddChannelToFolder={async (folderId) => {
            const nm = (await askInput({ title: 'New channel', label: 'Channel name', placeholder: 'general' }))?.trim();
            if (!nm) return;
            quickAddChannelToFolder(folderId, nm);
          }}
          onToggleFavorite={(chId) => toggleFav(currentVoidId, chId)}
          isFavorite={(chId) => isFav(currentVoidId, chId)}
          onMoveChannel={moveChannelToFolder}
          onRenameChannel={renameChannelInline}
          onDeleteChannel={deleteChannelInline}
          onChannelReorder={handleChannelReorder}
          onFolderReorder={handleFolderReorder}
          roles={spaceRoles}
          onUpdateChannelAccess={handleChannelAccessUpdate}
          onToggleChannelLock={handleChannelLockToggle}
          onSetHomeChannel={setDefaultChannel}
          footer={voiceDock}
          token={getAuthToken() || ''}
        />
      )}
      {/* DM sidebar suppressed on desktop — replaced by full-page view in main column */}
      {/* Resize handle only shown when space channels are visible */}
      {showChannelsColumn && (
        <div
          className="hidden md:block w-1 cursor-col-resize bg-transparent hover:bg-neutral-700/40"
          onMouseDown={(e) => startDrag('chan', e.clientX)}
          role="separator"
          aria-label="Resize channels column"
        />
      )}
    </>
  );
})()}

      {/* Desktop DM sidebar — visible alongside the main chat column */}
      {channelPaneView === 'dms' && (
        <DmSidebar
          dmSearch={dmSearch}
          dmSearchOpen={dmSearchOpen}
          setDmSearch={setDmSearch}
          setDmSearchOpen={setDmSearchOpen}
          friendsOnline={friendsOnline}
          friendsList={friendsList}
          dmStartBusyUserId={dmStartBusyUserId}
          activeDmVoids={activeDmVoids}
          msgsByKey={msgsByKey}
          unread={unread}
          notified={notified}
          currentVoidId={currentVoidId}
          meUserId={me?.userId}
          tabBgClass={tabBgClass}
          getDmDisplayName={getDmDisplayName}
          onStartDm={startDmWithUser}
          onOpenFriends={() => setFriendsOpen(true)}
          onOpenDm={(id) => { switchVoid(id); switchChannel('chat'); }}
        />
      )}
    </>
  );
}
